import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Clock, Droplets, HelpCircle, BookMarked, Newspaper, GraduationCap, Apple } from 'lucide-react';

const LearnPage = () => {
  const subsections = [
    {
      path: '/what-is-cll',
      title: 'What Is CLL/SLL',
      description: 'Understanding Chronic Lymphocytic Leukaemia and Small Lymphocytic Lymphoma - the basics of diagnosis, staging, and what it means for you.',
      icon: BookOpen
    },
    {
      path: '/watch-and-wait',
      title: 'Watch and Wait',
      description: 'Learn about active surveillance - why many people with CLL don\'t need immediate treatment and what monitoring involves.',
      icon: Clock
    },
    {
      path: '/blood-markers',
      title: 'Blood Markers',
      description: 'Understanding the blood tests used to monitor CLL - what WBC, lymphocytes, and other markers mean for your health.',
      icon: Droplets
    },
    {
      path: '/cll-diet',
      title: 'Eating Well with CLL',
      description: 'Practical nutrition guidance - foods to include and limit, tips for maintaining energy, and food safety considerations.',
      icon: Apple
    },
    {
      path: '/glossary',
      title: 'Glossary',
      description: 'A dictionary of medical terms related to CLL/SLL - from ALC to ZAP-70, explained in plain language.',
      icon: BookMarked
    },
    {
      path: '/common-questions',
      title: 'Common Questions',
      description: 'Answers to frequently asked questions about living with CLL/SLL, from newly diagnosed to long-term management.',
      icon: HelpCircle
    },
    {
      path: '/reading-learning',
      title: 'Reading & Learning',
      description: 'Curated resources for deeper learning - books, articles, and trusted sources about CLL/SLL.',
      icon: BookOpen
    },
    {
      path: '/updates-learning',
      title: 'Updates & Learning',
      description: 'Stay informed about new developments in CLL/SLL research, treatments, and community news.',
      icon: Newspaper
    }
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
          Learn About CLL/SLL
        </h1>
        
        {/* Introduction Section */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6 md:p-8 mb-6">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
            Knowledge is empowering
          </h2>
          
          <div className="space-y-4 text-base md:text-lg text-slate-700 leading-relaxed">
            <p>
              Understanding your condition can help you feel more confident in conversations with your healthcare team and more in control of your journey with CLL or SLL.
            </p>
            
            <p>
              This section covers the fundamentals - from understanding your diagnosis to interpreting blood test results. We've written everything in plain language, avoiding medical jargon where possible.
            </p>
            
            <p>
              Whether you're newly diagnosed or have been living with CLL/SLL for years, there's always more to learn. Take your time, explore what interests you, and remember - you're not alone in this.
            </p>
            
            <p className="font-medium text-slate-900 pt-2 border-t border-slate-200">
              These resources are educational and don't replace advice from your medical team. Always discuss your specific situation with your doctor.
            </p>
          </div>
        </div>
      </div>

      {/* Subsections Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {subsections.map((section) => {
          const Icon = section.icon;
          
          return (
            <Link
              key={section.path}
              to={section.path}
              className="group bg-white rounded-2xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all duration-200"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-50 rounded-xl group-hover:bg-blue-100 transition-colors">
                  <Icon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 group-hover:text-blue-700 transition-colors mb-2">
                    {section.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {section.description}
                  </p>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Bottom CTA */}
      <div className="bg-slate-50 rounded-2xl p-6 text-center">
        <GraduationCap className="h-10 w-10 text-slate-400 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Have a question not covered here?
        </h3>
        <p className="text-slate-600 mb-4">
          Check our Glossary for medical terms or Common Questions for more answers.
        </p>
        <div className="flex justify-center gap-3">
          <Link
            to="/glossary"
            className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
          >
            View Glossary
          </Link>
          <Link
            to="/common-questions"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Common Questions
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LearnPage;
